import {createContext, ReactElement, ReactNode, useCallback, useContext, useRef, useState} from "react";
import {getProductListBanners} from "./api";
import {getProductListBannerContext, getProductListBannerSetKey} from "./helpers";
import {ProductListBanner, ProductListBannersContextValue} from "./types";

const ProductListBannersContext = createContext<ProductListBannersContextValue | null>(null);

type ProductListBannersProviderProps = {
    children: ReactNode;
};

type ProductListBannersState = {
    activeSlide: number;
    bannerSetKey: string;
    banners: ProductListBanner[];
};

export function ProductListBannersProvider({children}: ProductListBannersProviderProps): ReactElement {
    const [state, setState] = useState<ProductListBannersState>({
        activeSlide: 0,
        bannerSetKey: "",
        banners: []
    });
    const requestVersion = useRef(0);
    const bannerSetKeyRef = useRef("");

    const applyBanners = useCallback((nextBanners: ProductListBanner[]): void => {
        const nextBannerSetKey = getProductListBannerSetKey(nextBanners);

        if (bannerSetKeyRef.current === nextBannerSetKey) {
            return;
        }

        bannerSetKeyRef.current = nextBannerSetKey;
        setState({
            activeSlide: 0,
            bannerSetKey: nextBannerSetKey,
            banners: nextBanners
        });
    }, []);

    const setActiveSlide = useCallback((currentBannerSetKey: string, slide: number): void => {
        if (bannerSetKeyRef.current === currentBannerSetKey) {
            setState((currentState) => currentState.bannerSetKey === currentBannerSetKey
                ? {...currentState, activeSlide: slide}
                : currentState
            );
        }
    }, []);

    const loadBanners = useCallback(async (filter?: string, filterId?: string): Promise<void> => {
        const context = getProductListBannerContext(filter, filterId);

        const currentRequestVersion = ++requestVersion.current;

        try {
            const nextBanners = await getProductListBanners(context ?? undefined);

            if (currentRequestVersion === requestVersion.current) {
                applyBanners(nextBanners);
            }
        } catch {
            // Existing banners remain visible when the request cannot be completed.
        }
    }, [applyBanners]);

    const value: ProductListBannersContextValue = {
        banners: state.banners,
        bannerSetKey: state.bannerSetKey,
        activeSlide: state.activeSlide,
        refreshForProductList: loadBanners,
        setActiveSlide
    };

    return (
        <ProductListBannersContext.Provider value={value}>
            {children}
        </ProductListBannersContext.Provider>
    );
}

export function useProductListBanners(): ProductListBannersContextValue {
    const context = useContext(ProductListBannersContext);

    if (!context) {
        throw new Error("useProductListBanners must be used inside ProductListBannersProvider.");
    }

    return context;
}
